import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, getCurrentUser } from "@/lib/apiClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { History } from "lucide-react";

interface AddEventDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  batchId: string;
  workOrderId?: string | null;
}

const eventTypes = [
  { value: "created", label: "Lote Creado" },
  { value: "material_added", label: "Material Agregado" },
  { value: "process_started", label: "Proceso Iniciado" },
  { value: "process_completed", label: "Proceso Completado" },
  { value: "quality_check", label: "Control de Calidad" },
  { value: "rework", label: "Retrabajo" },
  { value: "hold", label: "En Espera" },
  { value: "released", label: "Liberado" },
  { value: "shipped", label: "Enviado" },
  { value: "note", label: "Nota / Observacion" },
];

export function AddEventDialog({ open, onOpenChange, batchId, workOrderId }: AddEventDialogProps) {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    event_type: "",
    description: "",
    location: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.event_type) {
      toast.error("Selecciona un tipo de evento");
      return;
    }

    setLoading(true);
    try {
      const user = getCurrentUser();

      const { error } = await api.from("traceability_events").insert({
        batch_id: batchId,
        work_order_id: workOrderId || null,
        event_type: formData.event_type,
        description: formData.description || null,
        location: formData.location || null,
        performed_by: user?.id,
      });

      if (error) throw error;

      toast.success("Evento registrado correctamente");
      queryClient.invalidateQueries({ queryKey: ["traceability-events", batchId] });
      queryClient.invalidateQueries({ queryKey: ["traceability-events-global"] });

      onOpenChange(false);
      resetForm();
    } catch (error: any) {
      toast.error(error.message || "Error al registrar evento");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      event_type: "",
      description: "",
      location: "",
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Registrar Evento de Trazabilidad
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Tipo de Evento *</Label>
            <Select
              value={formData.event_type}
              onValueChange={(value) => setFormData({ ...formData, event_type: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar tipo..." />
              </SelectTrigger>
              <SelectContent>
                {eventTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Descripcion</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Detalle del evento..."
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label>Ubicacion</Label>
            <Input
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="Ej: Almacen A, Linea 1, etc"
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
                resetForm();
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Guardando..." : "Registrar Evento"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
